import * as React from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { CalendarIcon } from "lucide-react"
import { format, parseISO } from "date-fns"
import { cn } from "@/lib/utils"
import { Slider } from "@/components/ui/slider"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { CheckIcon, ChevronDownIcon } from "lucide-react"
import { useState } from "react"
import { get } from 'lodash'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import {
  Button as RAButton,
  DatePicker as RADatePicker,
  Dialog as RADialog,
  Group as RAGroup,
  Popover as RAPopover,
} from "react-aria-components"
import { Calendar as RACalendar } from "@/components/ui/calendar-rac"
import { DateInput } from "@/components/ui/datefield-rac"
import { parseDate, today, getLocalTimeZone } from "@internationalized/date"

const createChangeEvent = (name: string, value: string): React.ChangeEvent<HTMLInputElement> => ({
  target: { name, value },
  currentTarget: { name, value },
  nativeEvent: new Event('change'),
  type: 'change',
  bubbles: true,
  cancelable: true,
  defaultPrevented: false,
  isPropagationStopped: () => false,
  isDefaultPrevented: () => false,
  persist: () => {},
  preventDefault: () => {},
  stopPropagation: () => {},
  timeStamp: Date.now()
} as React.ChangeEvent<HTMLInputElement>)

type SelectOption = {
  value: string | number
  label: string
  description?: string
}

interface ErrorMessageProps {
  error?: string
}

export function ErrorMessage({ error }: ErrorMessageProps) {
  if (!error) return null
  return <p className="text-sm text-red-500 mt-1">{error}</p>
}

interface FormLabelProps {
  htmlFor: string
  label: string
  className?: string
  isReadOnly?: boolean
}

export function FormLabel({ htmlFor, label, className = "", isReadOnly }: FormLabelProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <Label htmlFor={htmlFor} className={className}>
        {label}
      </Label>
      {isReadOnly && (
        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
          readonly
        </span>
      )}
    </div>
  )
}

interface BaseFieldProps {
  name: string
  label: string
  data: Record<string, any>
  errors: Record<string, string>
  readOnly?: boolean
  required?: boolean
}

interface InputFieldProps extends BaseFieldProps {
  type?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

interface CheckboxFieldProps {
  name: string
  label: string
  data: any
  errors: any
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

interface TextareaFieldProps extends BaseFieldProps {
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
}

interface SelectFieldProps extends BaseFieldProps {
  options: SelectOption[]
  onValueChange: (name: string, value: string) => void
}

interface NumberInputFieldProps extends BaseFieldProps {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  prefix?: string
  suffix?: string
  placeholder?: string
}

interface RadioGroupFieldProps extends BaseFieldProps {
  options: SelectOption[]
  onChange: (name: string, value: string) => void
}

interface SteppedSliderFieldProps extends BaseFieldProps {
  max: number;
  value?: number;
  onChange: (e: { target: { name: string; value: number } }) => void;
}

interface SearchableSelectFieldProps extends BaseFieldProps {
  options: SelectOption[]
  onValueChange: (name: string, value: string | number) => void
}

interface PhoneInputFieldProps extends BaseFieldProps {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}

interface DateFieldProps extends BaseFieldProps {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export function InputField({
  name,
  label,
  type = "text",
  data,
  errors,
  onChange,
  readOnly,
  required
}: InputFieldProps) {
  const value = get(data, name)
  const error = get(errors, name)

  return (
    <div className="space-y-2">
      <FormLabel
        htmlFor={name}
        label={label}
        isReadOnly={readOnly}
      />
      <Input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        required={required}
        className={readOnly ? "bg-muted" : ""}
      />
      <ErrorMessage error={error} />
    </div>
  )
}

export const CheckboxField = ({ name, label, data, errors, onChange }: CheckboxFieldProps) => {
  const value = get(data, name)
  const error = get(errors, name)

  return (
    <div className="flex items-center space-x-2">
      <input
        id={name}
        name={name}
        type="checkbox"
        checked={Boolean(value)}
        onChange={onChange}
        className={`h-4 w-4 rounded border-gray-300 ${
          error ? 'border-red-500' : ''
        }`}
      />
      <div>
        <label htmlFor={name} className="block text-sm font-medium cursor-pointer">
          {label}
        </label>
        {error && (
          <p className="text-red-500 text-xs mt-1">{error}</p>
        )}
      </div>
    </div>
  )
}

export function TextareaField({
  name,
  label,
  data,
  errors,
  onChange,
  readOnly,
  required
}: TextareaFieldProps) {
  const value = get(data, name) ?? ''
  const error = get(errors, name)

  return (
    <div className="space-y-2">
      <FormLabel
        htmlFor={name}
        label={label}
        isReadOnly={readOnly}
      />
      <Textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        required={required}
        className={readOnly ? "bg-muted" : ""}
      />
      <ErrorMessage error={error} />
    </div>
  )
}

export function SelectField({
  name,
  label,
  data,
  errors,
  options,
  onValueChange,
  readOnly,
  required
}: SelectFieldProps) {
  const value = get(data, name)
  const error = get(errors, name)
  const id = React.useId()

  return (
    <div className="space-y-2">
      <FormLabel
        htmlFor={name}
        label={label}
        isReadOnly={readOnly}
      />
      <Select
        value={value}
        onValueChange={(val) => onValueChange(name, val)}
        disabled={readOnly}
        required={required}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ErrorMessage error={error} />
    </div>
  )
}

export function NumberInputField({
  name,
  label,
  data,
  errors,
  onChange,
  readOnly,
  required,
  prefix,
  suffix,
  placeholder = ""
}: NumberInputFieldProps) {
  const value = get(data, name)
  const error = get(errors, name)
  const hasPrefix = Boolean(prefix)
  const hasSuffix = Boolean(suffix)

  return (
    <div className="space-y-2">
      <FormLabel
        htmlFor={name}
        label={label}
        isReadOnly={readOnly}
      />
      <div className="relative">
        <Input
          id={name}
          name={name}
          type="number"
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          required={required}
          className={cn(
            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
            hasPrefix && "ps-6",
            hasSuffix && "pe-12",
            readOnly && "bg-muted"
          )}
          placeholder={placeholder}
        />
        {prefix && (
          <span className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-sm peer-disabled:opacity-50">
            {prefix}
          </span>
        )}
        {suffix && (
          <span className="text-muted-foreground pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-sm peer-disabled:opacity-50">
            {suffix}
          </span>
        )}
      </div>
      <ErrorMessage error={error} />
    </div>
  )
}

export function RadioGroupField({
  name,
  label,
  data,
  errors,
  options,
  onChange,
  readOnly,
  required
}: RadioGroupFieldProps) {
  const value = get(data, name)
  const error = get(errors, name)
  const id = React.useId()

  return (
    <div className="space-y-2">
      <FormLabel
        htmlFor={name}
        label={label}
        isReadOnly={readOnly}
      />
      <RadioGroup
        value={value}
        onValueChange={(val: string) => onChange(name, val)}
        disabled={readOnly}
        className="gap-6"
      >
        {options.map((option) => (
          <div key={option.value} className="flex items-start gap-2">
            <RadioGroupItem
              value={option.value}
              id={`${id}-${option.value}`}
              aria-describedby={`${id}-${option.value}-description`}
            />
            <div className="grid grow gap-2">
              <Label htmlFor={`${id}-${option.value}`}>
                {option.label}
              </Label>
              {option.description && (
                <p id={`${id}-${option.value}-description`} className="text-muted-foreground text-xs">
                  {option.description}
                </p>
              )}
            </div>
          </div>
        ))}
      </RadioGroup>
      <ErrorMessage error={error} />
    </div>
  )
}


export function SteppedSliderField({
  name,
  label,
  data,
  errors,
  onChange,
  max,
  value: propValue,
  readOnly,
  required
}: SteppedSliderFieldProps) {
  const value = propValue ?? get(data, name) ?? 0;
  const error = get(errors, name);
  const ticks = [...Array(max + 1)].map((_, i) => i);
  const skipInterval = Math.max(1, Math.floor(max / 6)); // Show about 6 labels

  return (
    <div className="space-y-2">
      <FormLabel
        htmlFor={name}
        label={label}
        isReadOnly={readOnly}
      />
      <div>
        <Slider
          value={[value]}
          max={max}
          min={0}
          step={1}
          onValueChange={(val) => onChange({ target: { name, value: val[0] } })}
          aria-label={label}
          disabled={readOnly}
        />
        <span
          className="text-muted-foreground mt-3 flex w-full items-center justify-between gap-1 px-2.5 text-xs font-medium"
          aria-hidden="true"
        >
          {ticks.map((_, i) => (
            <span key={i} className="flex w-0 flex-col items-center justify-center gap-2">
              <span
                className={cn("bg-muted-foreground/70 h-1 w-px", i % skipInterval !== 0 && "h-0.5")}
              />
              <span className={cn(i % skipInterval !== 0 && "opacity-0")}>{i}</span>
            </span>
          ))}
        </span>
      </div>
      <ErrorMessage error={error} />
    </div>
  );
}

export function SearchableSelectField({
  name,
  label,
  data,
  errors,
  options,
  onValueChange,
  readOnly
}: SearchableSelectFieldProps) {
  const value = get(data, name)
  const error = get(errors, name)
  const id = React.useId()
  const [open, setOpen] = useState<boolean>(false)

  return (
    <div className="space-y-2">
      <FormLabel
        htmlFor={name}
        label={label}
        isReadOnly={readOnly}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between",
              readOnly && "bg-muted"
            )}
            disabled={readOnly}
          >
            <span className={cn("truncate", !value && "text-muted-foreground")}>
              {value
                ? options.find((option) => option.value.toString() === value.toString())?.label
                : "Select an option"}
            </span>
            <ChevronDownIcon
              size={16}
              className="text-muted-foreground/80 shrink-0"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-full min-w-[var(--radix-popper-anchor-width)] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value.toString()}
                    onSelect={(currentValue) => {
                      onValueChange(name, currentValue === value.toString() ? "" : option.value)
                      setOpen(false)
                    }}
                  >
                    {option.label}
                    {value.toString() === option.value.toString() && <CheckIcon size={16} className="ml-auto" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ErrorMessage error={error} />
    </div>
  )
}

export function PhoneInputField({
  name,
  label,
  data,
  errors,
  onChange,
  readOnly,
  required
}: PhoneInputFieldProps) {
  const value = get(data, name)
  const error = get(errors, name)

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target
    if (!value) return

    try {
      const parsed = parsePhoneNumberFromString(value, 'US')
      if (parsed) {
        const formattedValue = parsed.country === 'US'
          ? parsed.formatNational()
          : parsed.formatInternational()
        onChange(createChangeEvent(name, formattedValue))
      }
    } catch (e) {
      // Keep original value if parsing fails
    }
  }

  return (
    <div className="space-y-2">
      <FormLabel
        htmlFor={name}
        label={label}
        isReadOnly={readOnly}
      />
      <Input
        id={name}
        name={name}
        type="tel"
        value={value}
        onChange={onChange}
        onBlur={handleBlur}
        readOnly={readOnly}
        required={required}
        className={readOnly ? "bg-muted" : ""}
      />
      <ErrorMessage error={error} />
    </div>
  )
}

export function DateField({
  name,
  label,
  data,
  errors,
  onChange,
  readOnly,
  required
}: DateFieldProps) {
  const value = get(data, name)
  const error = get(errors, name)

  const handleDateSelect = (date: any) => {
    if (!onChange) return;
    // Convert CalendarDate to string format
    const formattedDate = date ? format(date.toDate(getLocalTimeZone()), "yyyy-MM-dd") : ""
    onChange(createChangeEvent(name, formattedDate))
  }

  // Convert the stored date string to a CalendarDate object for the calendar
  const selectedDate = value ? parseDate(value) : undefined

  return (
    <div className="space-y-2">
      <FormLabel
        htmlFor={name}
        label={label}
        isReadOnly={readOnly}
      />
      <RADatePicker
        className="*:not-first:mt-2"
        value={selectedDate}
        onChange={handleDateSelect}
        isDisabled={readOnly}
        isRequired={required}
      >
        <div className="flex">
          <RAGroup className="w-full">
            <DateInput className="pe-9" />
          </RAGroup>
          <RAButton
            className={cn(
              "text-muted-foreground/80 hover:text-foreground data-focus-visible:border-ring data-focus-visible:ring-ring/50 z-10 -ms-9 -me-px flex w-9 items-center justify-center rounded-e-md transition-[color,box-shadow] outline-none data-focus-visible:ring-[3px]",
              readOnly && "opacity-50"
            )}
          >
            <CalendarIcon size={16} />
          </RAButton>
        </div>
        <RAPopover
          className="bg-background text-popover-foreground data-entering:animate-in data-exiting:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0 data-[entering]:zoom-in-95 data-[exiting]:zoom-out-95 data-[placement=bottom]:slide-in-from-top-2 data-[placement=left]:slide-in-from-right-2 data-[placement=right]:slide-in-from-left-2 data-[placement=top]:slide-in-from-bottom-2 z-50 rounded-lg border shadow-lg outline-hidden"
          offset={4}
        >
          <RADialog className="max-h-[inherit] overflow-auto p-2">
            <RACalendar />
          </RADialog>
        </RAPopover>
      </RADatePicker>
      <ErrorMessage error={error} />
    </div>
  )
}
